import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDeleteUser, useUpdateUser, useUsersData } from '../../../../hooks/user'
import { useForm } from 'react-hook-form'
import { getErrorResponse, getTimeRelativeTime, getUpdatedSearchParams, parseSearchParams } from '../../../../services/util'
import { AddUpdateUser } from './AddUpdateUserDialog'
import { twElementStyles } from '../../../../twElementStyles'
import { usePopup } from '../../../../context/popupContext'
import { RenderDefaultActions, RenderStatus } from '../../common'
import { CustomTable } from '../../../../components/customTable'
import { FilterOptions } from '../../../../components/customTable/custom-table-interfaces'
import { useNotificationBar } from '../../../../context/notificationContext'
import { useRolesData } from '../../../../hooks/role'
import { SmoothTransition } from '../../../../components/pageTransition'
import { Unauthorized } from '../../../Unauthorized'
import { PagePreloader } from '../../../../components/pagePreloader'
import { useClientData } from '../../../../hooks/client-manage'
export interface Response {
    records: any[],
    pages?: number,
    total?: number,
    pagination?: Object
}
export const UsersList = () => {
    let [searchParams, setSearchParams] = useSearchParams()
    let filterParams: any = parseSearchParams(searchParams, 'filter')
    const notification = useNotificationBar()
    let [addUpdate, setAddUpdate] = useState<any>(false)
    let [addUpdateInput, setaddUpdateInput] = useState<any>(null)
    let [editForm, setEditForm] = useState<any>(false)
    let popup = usePopup()
    let { data, isFetching: isLoading, refetch, isError, isSuccess, error, dataUpdatedAt } = useUsersData(filterParams, {
        keepPreviousData: true
    })

    const { data: roles } = useRolesData({
        pagination: {
            pageSize: 999,
            pageIndex: 0
        },
        sort: {
            id: 'role_name',
            desc: false
        }
    })

    const rolesData: any[] = roles?.data?.results ?? []

    let { mutate: deleteUser } = useDeleteUser()
    let { mutate: updateUser } = useUpdateUser()
    const openAddUpdateDialog = (input: any, string: any) => {
        setaddUpdateInput(input)
        setAddUpdate(true)
        if (string === 'edit') {
            setEditForm(true)
        } else {
            setEditForm(false)
        }
    }

    let useFormHook = useForm({
        defaultValues: filterParams
    })

    let responseData: any = data?.data;

    const onDeleteAction = (row: any) => {
        console.log(row);
        popup.open({
            title: 'Are you sure ?',
            message: `Do you want to delete user ${row?.first_name} ${row?.last_name} ? This cannot be undone.`,
            type: 'confirm',
            onAction: (value: boolean) => {
                if (value) {
                    deleteUser({
                        id: row?.id
                    }, {
                        onSuccess: () => {
                            notification.push({
                                title: `User deleted !`,
                                message: `User ${row?.first_name} ${row?.last_name} has been deleted successfully.`,
                                type: 'info'
                            })
                            refetch();
                        },
                        onError(error) {
                            notification.push(getErrorResponse(error))
                        }
                    })
                }
            },
            confirmButtonText: 'Delete',
            confirmButtonClass: twElementStyles.twButtonElegantRed
        })
    }

    const onStatusChange = async (row: any) => {
        let update: any = { is_active: row.is_active ? 0 : 1 }
        popup.open({
            title: `Are you sure ?`,
            message: `Do you want to ${update.is_active ? 'Activate' : 'Deactivate'} user ${row.first_name} ${row.last_name} ?`,
            type: 'confirm',
            onAction: (value: any) => {
                if (value) {
                    updateUser({
                        data: update,
                        id: row.id
                    }, {
                        onSuccess: () => {
                            notification.push({
                                title: `User ${update.is_active ? 'activation success !' : 'deactivated !'} `,
                                message: `User ${row.first_name} ${row.last_name} ${update.is_active ? 'activated' : 'deactivated'} successfully !`,
                                type: update.is_active ? 'success' : 'info'
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        }
                    })
                }
            }
        })

    }

    const onAction = (actionType: any, row: any) => {
        if (actionType === 'edit') openAddUpdateDialog(row, 'edit')
        if (actionType === 'delete') onDeleteAction(row)
    }

    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'first_name',
            header: () => 'FIRST NAME',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'last_name',
            header: () => 'LAST NAME',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'email',
            header: () => 'EMAIL',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'role_name',
            header: () => 'ROLE',
            footer: (info) => info.column.id,
        },
        {
            id: 'channel_name',
            accessorFn: (value) => value.channel_name ?? 'All',
            header: () => 'CHANNEL',
            footer: (info) => info.column.id,
            enableSorting: false
        },
        {
            id: 'is_active',
            header: 'STATUS',
            accessorKey: 'is_active',
            cell: ({ row }) => RenderStatus(row, 'is_active', onStatusChange),
            enableSorting: false
        },

        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) => RenderDefaultActions(row, ['edit', 'delete'], onAction)
        },

    ]

    const mobileViewOptions = {
        mainTitle: {
            accessKey: 'email'
        }
    }

    const rowSelectionState = useState<RowSelectionState>({})

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(filterParams, values, name)
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                }
                else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const onDataAddUpdateAction = (values: any) => {
        setAddUpdate(false);
        setaddUpdateInput(null);
        refetch();
    }

    const filterOptions: FilterOptions[] = [{
        key: 'is_active',
        options: [
            {
                display: `All`,
                value: ''
            },
            {
                display: `Active`,
                value: 1
            },
            {
                display: `Inactive`,
                value: 0
            }
        ],
        label: 'Status'
    },
    {
        key: 'role_id',
        options: [
            {
                display: `All`,
                value: ''
            },
            ...rolesData.map(role => ({
                display: role.role_name,
                value: role.id
            }))
        ],
        label: 'Role'
    }

    ]
    useEffect(() => {
        const handlePrintScreen = (e) => {
            if (e.key === 'PrintScreen') {
                navigator.clipboard.writeText('');
                alert('Screenshots disabled!');
            }
        };

        const handleCtrlP = (e) => {
            if (e.ctrlKey && e.key === 'p') {
                alert('This section is not allowed to print or export to PDF');
                e.preventDefault();
                e.stopPropagation();
            }
        };

        document.addEventListener('keyup', handlePrintScreen);
        document.addEventListener('keydown', handleCtrlP);

        return () => {
            document.removeEventListener('keyup', handlePrintScreen);
            document.removeEventListener('keydown', handleCtrlP);
        };
    }, []);

    // document.addEventListener('keyup', (e) => {
    //     if (e.key == 'PrintScreen') {
    //         navigator.clipboard.writeText('');
    //         alert('Screenshots disabled!');
    //     }
    // });

    // /** TO DISABLE PRINTS WHIT CTRL+P **/
    // document.addEventListener('keydown', (e) => {
    //     if (e.ctrlKey && e.key == 'p') {
    //         alert('This section is not allowed to print or export to PDF');
    //         e.cancelBubble = true;
    //         e.preventDefault();
    //         e.stopImmediatePropagation();
    //     }
    // });
    return (
        (isError || isSuccess) ?
            <SmoothTransition>
                {
                    (error as any)?.response?.data?.statusCode === 403 ?
                        <Unauthorized /> :
                        <div className='w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md '>
                            <AddUpdateUser isEditForm={editForm} rolesData={rolesData} onSubmitHandler={onDataAddUpdateAction} input_data={addUpdateInput} isOpen={!!addUpdate} setIsOpen={setAddUpdate} />
                            <aside className="flex flex-auto items-center">
                                <CustomTable
                                    title='User Management'
                                    titleLabel={(!isError) ? `Last fetched on ${getTimeRelativeTime(dataUpdatedAt)}` : ''}
                                    formHook={useFormHook}
                                    columns={columns}
                                    data={responseData?.results}
                                    pages={responseData?.pages}
                                    totalRecords={responseData?.total}
                                    onRefresh={refetch}
                                    isLoading={isLoading}
                                    isErrored={isError}
                                    rowSelectionState={rowSelectionState}
                                    mobileViewOptions={mobileViewOptions}
                                    useFieldForSelect='id'
                                    filterOptions={filterOptions}
                                    onAddAction={() => {
                                        setaddUpdateInput({});
                                        setAddUpdate(true)
                                        setEditForm(false);
                                    }}
                                />
                            </aside>
                        </div>
                }
            </SmoothTransition>
            : <PagePreloader />
    )
}
